import { ChatBot, NextPage, NextPageHead, OfficeLocationContextProvider, PageView } from '@aspendental/shared-components-web';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { getEnrichedPageContents, getRootLevelConfig, setCacheHeader } from '../utils';
import getConfigServices from '../utils/get-config-services';
import { NOT_FOUND } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, LandingPageByAppNameWithPathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { getDeviceTypeFromHeaders } from '@src/pages/utils/get-device-type-from-headers';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import { getUserCoordinates } from '@src/pages/utils/get-user-coordinates';
import { getProvidersByIds } from '@src/services/bffFacilityCalls';
import { TDoctor } from '@src/services/getLocations';
const UpdateLivePreviewData = dynamic(() => import('@src/components/shared/providers/ContentfulLivePreview').then(mod => mod.UpdateLivePreviewData));
const PhysiciansHomePage = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  if (props.preview) {
    return <UpdateLivePreviewData InternalComponent={InternalPhysiciansHomePage} data={props} />;
  }
  return <InternalPhysiciansHomePage {...props} data-sentry-element="InternalPhysiciansHomePage" data-sentry-component="PhysiciansHomePage" data-sentry-source-file="index.page.tsx" />;
};
const InternalPhysiciansHomePage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    appData,
    page,
    config,
    officeInfo,
    breadcrumbsData,
    pageReferrer
  } = props;
  const pageData = page;
  const {
    url: logoUrl
  } = appData?.logo ?? {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = pageData;
  const {
    features,
    featureFlags,
    siteURL,
    logo
  } = config as BrandThemeConfig;
  return <OfficeLocationContextProvider officeInfo={officeInfo?.length ? officeInfo[0] : {}} data-sentry-element="OfficeLocationContextProvider" data-sentry-component="InternalPhysiciansHomePage" data-sentry-source-file="index.page.tsx">
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Office Details" data-sentry-element="PageView" data-sentry-source-file="index.page.tsx" />
			<NextPageHead path={path} title={title} indexed={indexed} description={description} canonical={canonical} originUrl={siteURL} breadcrumbTitle={breadcrumbsData.breadcrumbTitle} breadcrumbs={breadcrumbsData.breadcrumbsCollection.items} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="index.page.tsx" />
			<NextPage pageData={{
      ...pageData
    }} data-sentry-element="NextPage" data-sentry-source-file="index.page.tsx" />
			{features.chat.enabled && !disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</OfficeLocationContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  req,
  res,
  preview = false
}) => {
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const {
    logger,
    brandConfig,
    gqlClient,
    appName
  } = getRootLevelConfig({
    req,
    preview,
    loggerName: 'providers-home-page'
  });
  const path = brandConfig.features.providers.root;
  logger.info(path);
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const [appPagesData, appMetadata, pageData, translations] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(LandingPageByAppNameWithPathDocument, {
      appName,
      path,
      preview
    }).toPromise(), getServerSideTranslations(locale)]);
    const appData = {
      ...appPagesData.data?.appCollection?.items[0],
      ...appMetadata.data?.appCollection?.items[0]
    };

    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      logger.error('CONTENTFUL_ERROR_PROVIDERS_HOME_PAGE_APPDATA_ERROR');
      return NOT_FOUND;
    }
    if (pageData.error) {
      logger.error(`CONTENTFUL_ERROR_PROVIDERS_HOME_PAGE: PATH - ${path} - ERROR: ${pageData.error}`);
    }
    const page = pageData.data.pageCollection?.items[0] as PageFragment;
    if (!page) {
      logger.error(`COULD_NOT_FIND_THE_PAGE_WITH_PATH: ${path}`);
      return NOT_FOUND;
    }
    let officeInfo: {
      doctors: TDoctor[];
    }[] = [];
    if (page?.contentsCollection?.items?.find(item => item?.__typename === 'MeetOurStaff')?.providerIDs) {
      const transformedServices = getConfigServices(brandConfig);
      const beffeGraphQlUrl = process.env.ENVIRONMENT_NAME === 'prod' ? transformedServices.prod.BEFFE_GRAPHQL_URL : transformedServices.nonprod.BEFFE_GRAPHQL_URL;
      const providerIDs = page?.contentsCollection?.items?.find(item => item?.__typename === 'MeetOurStaff')?.providerIDs;
      if (providerIDs) {
        const providersByIDs = await getProvidersByIds(brandConfig.bffBrandType, providerIDs, beffeGraphQlUrl);
        officeInfo = [{
          doctors: providersByIDs?.data?.providersByIdList
        }];
      }
    }
    const enrichedPageContents = await getEnrichedPageContents({
      page,
      gqlClient,
      preview,
      logger
    });
    setCacheHeader(res, preview);
    return {
      props: {
        ...translations,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: {
          ...page,
          contentsCollection: {
            items: enrichedPageContents
          }
        },
        appData,
        user: getUserCoordinates(req, logger),
        officeInfo,
        footerData: appData?.footer,
        showHeaderNavMenu: true,
        path,
        breadcrumbsData: {
          breadcrumbTitle: 'Providers',
          breadcrumbsCollection: {
            items: [{
              breadcrumbTitle: 'Home',
              path: '/'
            }]
          }
        },
        preview,
        deviceType,
        pageReferrer,
        stateCodeFromHeaders
      }
    };
  } catch (e) {
    logger.error(`PROVIDERS_HOME_PAGE_LOAD_FAILURE: PATH - ${path} ERROR - ${e}`);
    return NOT_FOUND;
  }
};
export default PhysiciansHomePage;